import React, { useState, useEffect } from 'react';
import { Search, Plus, Building2, Filter } from 'lucide-react';
import { propertyService } from '../../services/propertyService';
import communeServiceDefault from '../../services/communeService';
import { useAuth } from '../../contexts/AuthContext';
import PropertyFormModal from './components/PropertyFormModal';
import PropertyTable from './components/PropertyTable';
import BulkActionsPanel from './components/BulkActionsPanel';
import PropertyFilters from './components/PropertyFilters';
import PropertyStats from './components/PropertyStats';

const AdvancedPropertyManagementInterface = () => {
  const { user, userProfile } = useAuth();
  const [properties, setProperties] = useState([]);
  const [communes, setCommunes] = useState([]);
  const [filteredProperties, setFilteredProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProperties, setSelectedProperties] = useState([]);
  const [showFormModal, setShowFormModal] = useState(false);
  const [editingProperty, setEditingProperty] = useState(null);
  const [stats, setStats] = useState(null);
  const [error, setError] = useState('');

  // Filter states
  const [filters, setFilters] = useState({
    search: '',
    commune: '',
    status: '',
    type: '',
    priceMin: '',
    priceMax: '',
    sortBy: 'created_at',
    sortOrder: 'desc'
  });

  // Load initial data with proper service calls
  const loadData = async () => {
    setLoading(true);
    try {
      const [propertiesResult, communesResult] = await Promise.all([
        propertyService?.getAllProperties(),
        communeServiceDefault?.getAllCommunes()
      ]);
      
      // Handle service response structure
      if (Array.isArray(propertiesResult)) {
        setProperties(propertiesResult);
      } else if (propertiesResult?.data && Array.isArray(propertiesResult?.data)) {
        setProperties(propertiesResult?.data);
        if (propertiesResult?.error) {
          setError(propertiesResult?.error);
        }
      } else {
        setProperties([]);
        setError('Invalid properties data format');
      }

      if (Array.isArray(communesResult)) {
        setCommunes(communesResult);
      } else if (communesResult?.data && Array.isArray(communesResult?.data)) {
        setCommunes(communesResult?.data);
        if (communesResult?.error) {
          setError(communesResult?.error);
        }
      } else {
        setCommunes([]);
      }

      // Try to get stats if available
      try {
        if (propertyService?.getPropertyStats) {
          const statsResult = await propertyService?.getPropertyStats();
          if (statsResult?.data) {
            setStats(statsResult?.data);
          } else if (statsResult && !statsResult?.error) {
            setStats(statsResult);
          }
        }
      } catch (statsError) {
        console.warn('Property stats not available:', statsError);
      }

    } catch (error) {
      setError('Erreur lors du chargement des données');
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter properties based on current filters
  useEffect(() => {
    let filtered = [...properties];

    // Search filter
    if (filters?.search) {
      const searchLower = filters?.search?.toLowerCase();
      filtered = filtered?.filter(property => 
        property?.title?.toLowerCase()?.includes(searchLower) ||
        property?.address?.toLowerCase()?.includes(searchLower) ||
        property?.description?.toLowerCase()?.includes(searchLower) ||
        property?.agent?.full_name?.toLowerCase()?.includes(searchLower)
      );
    }

    // Commune filter
    if (filters?.commune) {
      filtered = filtered?.filter(property => property?.commune_id === filters?.commune);
    }

    // Status filter
    if (filters?.status) {
      filtered = filtered?.filter(property => property?.status === filters?.status);
    }

    // Type filter
    if (filters?.type) {
      filtered = filtered?.filter(property => property?.property_type === filters?.type);
    }

    // Price range filter
    if (filters?.priceMin) {
      const minPrice = parseFloat(filters?.priceMin);
      filtered = filtered?.filter(property => parseFloat(property?.price) >= minPrice);
    }

    if (filters?.priceMax) {
      const maxPrice = parseFloat(filters?.priceMax);
      filtered = filtered?.filter(property => parseFloat(property?.price) <= maxPrice);
    }

    // Sorting
    filtered?.sort((a, b) => {
      const aValue = a?.[filters?.sortBy];
      const bValue = b?.[filters?.sortBy];
      
      let comparison = 0;
      if (aValue < bValue) comparison = -1;
      if (aValue > bValue) comparison = 1;
      
      return filters?.sortOrder === 'desc' ? -comparison : comparison;
    });

    setFilteredProperties(filtered);
  }, [properties, filters]);

  useEffect(() => {
    loadData();
  }, []);

  // Handle property CRUD operations with enhanced error handling
  const handlePropertySave = async (propertyData) => {
    try {
      let result;
      if (editingProperty) {
        result = await propertyService?.updateProperty(editingProperty?.id, propertyData);
      } else {
        result = await propertyService?.createProperty(propertyData);
      }

      // Handle different response formats
      if (result?.error) {
        setError(result?.error);
        return false;
      }

      if (result?.data || result) {
        await loadData();
        setShowFormModal(false);
        setEditingProperty(null);
        return true;
      }

      setError('Unexpected response format');
      return false;
    } catch (error) {
      setError('Erreur lors de la sauvegarde de la propriété');
      console.error('Error saving property:', error);
      return false;
    }
  };

  const handlePropertyDelete = async (property) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer la propriété "${property?.title}" ?`)) {
      return;
    }

    try {
      let result = await propertyService?.deleteProperty(property?.id);
      
      if (result?.error) {
        setError(result?.error);
        return;
      }

      await loadData();
      setSelectedProperties(prev => prev?.filter(id => id !== property?.id));
    } catch (error) {
      setError('Erreur lors de la suppression de la propriété');
      console.error('Error deleting property:', error);
    }
  };

  // Handle bulk actions
  const handleBulkAction = async (action, actionData) => {
    try {
      const updatePromises = selectedProperties?.map(propertyId => {
        switch (action) {
          case 'status':
            return propertyService?.updateProperty(propertyId, { status: actionData?.status });
          case 'commune':
            return propertyService?.updateProperty(propertyId, { commune_id: actionData?.communeId });
          case 'agent':
            return propertyService?.updateProperty(propertyId, { agent_id: actionData?.agentId });
          case 'delete':
            return propertyService?.deleteProperty(propertyId);
          default:
            return Promise.resolve();
        }
      });

      await Promise.all(updatePromises);
      await loadData();
      setSelectedProperties([]);
    } catch (error) {
      setError(`Erreur lors de l'action groupée`);
      console.error('Error in bulk action:', error);
    }
  };

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleFilterReset = () => {
    setFilters({
      search: '',
      commune: '',
      status: '',
      type: '',
      priceMin: '',
      priceMax: '',
      sortBy: 'created_at',
      sortOrder: 'desc'
    });
  };

  // Check if user can manage properties
  const canManageProperties = userProfile?.role === 'admin' || userProfile?.role === 'agent';

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement de l'interface de gestion...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Building2 className="h-8 w-8 text-blue-600" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Gestion Avancée des Propriétés</h1>
                <p className="text-sm text-gray-600">Interface d'administration avec organisation par communes</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-600">
                {filteredProperties?.length} / {properties?.length} propriétés
              </span>
              
              {canManageProperties && (
                <button
                  onClick={() => {
                    setEditingProperty(null);
                    setShowFormModal(true);
                  }}
                  className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Nouvelle Propriété
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {error}
            <button 
              onClick={() => setError('')}
              className="float-right font-bold text-red-700 hover:text-red-900"
            >
              ×
            </button>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Statistics */}
        {stats && <PropertyStats stats={stats} />}

        {/* Filters */}
        <div className="mt-6">
          <PropertyFilters
            filters={filters}
            communes={communes}
            onFilterChange={handleFilterChange}
            onFilterReset={handleFilterReset}
          />
        </div>

        {/* Bulk Actions */}
        {selectedProperties?.length > 0 && canManageProperties && (
          <div className="mt-6">
            <BulkActionsPanel
              selectedCount={selectedProperties?.length}
              communes={communes}
              onBulkAction={handleBulkAction}
            />
          </div>
        )}

        {/* Properties Table */}
        <div className="mt-6">
          <PropertyTable
            properties={filteredProperties}
            communes={communes}
            selectedProperties={selectedProperties}
            onSelectionChange={setSelectedProperties}
            onEdit={(property) => {
              setEditingProperty(property);
              setShowFormModal(true);
            }}
            onDelete={handlePropertyDelete}
            canManage={canManageProperties}
          />
        </div>
      </div>

      {/* Property Form Modal */}
      {showFormModal && (
        <PropertyFormModal
          property={editingProperty}
          communes={communes}
          onSave={handlePropertySave}
          onClose={() => {
            setShowFormModal(false);
            setEditingProperty(null);
          }}
        />
      )}
    </div>
  );
};

export default AdvancedPropertyManagementInterface;